import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { WalletIcon, ArrowDownIcon, ArrowUpIcon } from 'lucide-react';
import { useFountain } from './hooks/useFountain';
import { formatAddress } from './utils/helpers';
import web3Provider from './utils/web3Provider';
import { TokenLogos } from './assets/tokenLogos';

function FountainPage() {
  const [account, setAccount] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');

  const {
    stakedBalance,
    walletBalance,
    earnedRewards,
    depositToken,
    depositFee,
    isLoading,
    canWithdrawStatus,
    lockupInfo,
    fetchBalances,
    deposit,
    withdraw,
  } = useFountain();

  useEffect(() => {
    const checkConnection = async () => {
      if (window.ethereum && window.ethereum.selectedAddress) {
        try {
          const connectionResult = await web3Provider.connect();
          setAccount(connectionResult.account);
          await fetchBalances(connectionResult.account);
        } catch (error) {
          console.error('Auto-connection failed:', error);
        }
      }
    };

    checkConnection();

    if (window.ethereum) {
      window.ethereum.on('accountsChanged', async (accounts) => {
        if (accounts.length === 0) {
          setAccount(null);
        } else {
          setAccount(accounts[0]);
          await fetchBalances(accounts[0]);
        }
      });
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeAllListeners('accountsChanged');
      }
    };
  }, [fetchBalances]);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const result = await web3Provider.connect();
      setAccount(result.account);
      await fetchBalances(result.account);
    } catch (error) {
      console.error('Connection error:', error);
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDeposit = async () => {
    if (!depositAmount || parseFloat(depositAmount) <= 0) return;
    try {
      await deposit(depositAmount);
      setDepositAmount('');
    } catch (error) {
      console.error('Deposit failed:', error);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0) return;
    try {
      await withdraw(withdrawAmount);
      setWithdrawAmount('');
    } catch (error) {
      console.error('Withdrawal failed:', error);
    }
  };

  const setMaxDeposit = () => {
    setDepositAmount(walletBalance);
  };

  const setMaxWithdraw = () => {
    setWithdrawAmount(stakedBalance);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-950 to-black relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-40 right-20 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <main className="relative z-10">
        <div className="container mx-auto px-4 py-8">
          {/* Back Button */}
          <div className="mb-8">
            <a
              href="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors text-sm font-medium"
            >
              ← Back to Swap
            </a>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-6">
              <img src={TokenLogos.GIGACHAD} alt="Fountain" className="w-16 h-16 mr-4" />
              <h1 className="text-6xl font-bold text-white font-orbitron">
                Fountain
              </h1>
            </div>
            <p className="text-xl text-blue-200 font-rajdhani">
              Stake and Withdraw
            </p>
            <div className="mt-4 flex items-center justify-center gap-4">
              <a 
                href="/v1"
                className="text-blue-400 hover:text-blue-300 underline text-sm"
              >
                V1 Swap
              </a>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-4xl mx-auto">
            {/* Connect Wallet Prompt or Account Display */}
            {!account ? (
              <div className="mb-8 p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl text-center">
                <p className="text-white/60 mb-4">Connect your wallet to view balances and interact with Fountain</p>
                <button
                  onClick={handleConnect}
                  disabled={isConnecting}
                  className="inline-flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <WalletIcon className="w-5 h-5" />
                  <span>{isConnecting ? 'Connecting...' : 'Connect Wallet'}</span>
                </button>
              </div>
            ) : (
              <div className="mb-6 p-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <WalletIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-white/60 text-xs">Connected Wallet</div>
                      <div className="text-white font-mono text-sm">{formatAddress(account)}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-white/60 text-xs">Staked LSHARE</div>
                    <div className="text-xl font-bold text-white">{parseFloat(stakedBalance).toFixed(8)}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <div className="text-white/60 text-sm mb-2">Your Deposit</div>
                  <div className="text-3xl font-bold text-white">
                    {parseFloat(stakedBalance).toFixed(8)}
                  </div>
                  <div className="text-white/40 text-sm mt-1">
                    {depositToken?.symbol || '...'}
                  </div>
                </div>

                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <div className="text-white/60 text-sm mb-2">Wallet Balance</div>
                  <div className="text-3xl font-bold text-white">
                    {parseFloat(walletBalance).toFixed(8)}
                  </div>
                  <div className="text-white/40 text-sm mt-1">
                    {depositToken?.symbol || '...'}
                  </div>
                </div>

                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <div className="text-white/60 text-sm mb-2">Rewards Earned</div>
                  <div className="text-3xl font-bold text-green-400">
                    {parseFloat(earnedRewards).toFixed(8)}
                  </div>
                  <div className="text-white/40 text-sm mt-1">Tokens</div>
                </div>
              </div>

              {/* Deposit & Withdraw Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Deposit Card */}
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center mb-4">
                    <ArrowDownIcon className="w-5 h-5 text-green-400 mr-2" />
                    <h2 className="text-xl font-bold text-white">Deposit</h2>
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between mb-2">
                      <label className="text-white/60 text-sm">Amount</label>
                      <button
                        onClick={setMaxDeposit}
                        disabled={!account}
                        className="text-blue-400 hover:text-blue-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        MAX
                      </button>
                    </div>
                    <input
                      type="number"
                      value={depositAmount}
                      onChange={(e) => setDepositAmount(e.target.value)}
                      placeholder="0.0"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-lg focus:outline-none focus:border-blue-400"
                    />
                    {account && (
                      <div className="text-white/60 text-sm mt-2">
                        Available: {parseFloat(walletBalance).toFixed(8)} LSHARE
                      </div>
                    )}
                  </div>

                  <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-200 text-xs">
                      ⚠️ There is a {depositFee}% deposit fee for this staking pool (taken by Lif3 Protocol). We don't charge any additional fees on this deposit.
                    </p>
                  </div>

                  <button
                    onClick={handleDeposit}
                    disabled={!account || isLoading || !depositAmount || parseFloat(depositAmount) <= 0}
                    className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl transition-colors"
                  >
                    {!account ? 'Connect Wallet' : isLoading ? 'Processing...' : 'Deposit'}
                  </button>
                </div>

                {/* Withdraw Card */}
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                  <div className="flex items-center mb-4">
                    <ArrowUpIcon className="w-5 h-5 text-red-400 mr-2" />
                    <h2 className="text-xl font-bold text-white">Withdraw</h2>
                  </div>

                  <div className="mb-4">
                    <div className="flex justify-between mb-2">
                      <label className="text-white/60 text-sm">Amount</label>
                      <button
                        onClick={setMaxWithdraw}
                        disabled={!account}
                        className="text-blue-400 hover:text-blue-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        MAX
                      </button>
                    </div>
                    <input
                      type="number"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      placeholder="0.0"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-lg focus:outline-none focus:border-blue-400"
                    />
                    {account && (
                      <div className="text-white/60 text-sm mt-2">
                        Staked: {parseFloat(stakedBalance).toFixed(8)} LSHARE
                      </div>
                    )}
                  </div>

                  <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-blue-200 text-xs">
                      ℹ️ No fees on withdrawal. Your rewards will be automatically claimed when you withdraw.
                    </p>
                  </div>

                  {!canWithdrawStatus && parseFloat(stakedBalance) > 0 && (
                    <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-yellow-200 text-xs">
                        🔒 Your tokens are currently locked. You must wait {lockupInfo?.withdrawLockupEpochs || 6} epochs after deposit before withdrawing.
                      </p>
                    </div>
                  )}

                  <button
                    onClick={handleWithdraw}
                    disabled={!account || isLoading || !withdrawAmount || parseFloat(withdrawAmount) <= 0 || !canWithdrawStatus}
                    className="w-full bg-red-500 hover:bg-red-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl transition-colors"
                  >
                    {!account ? 'Connect Wallet' : isLoading ? 'Processing...' : !canWithdrawStatus ? 'Locked' : 'Withdraw'}
                  </button>
                </div>
              </div>

              {/* Connected Wallet Info */}
              {account && (
                <div className="mt-8 text-center">
                  <div className="inline-flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-white text-sm font-medium">
                      {formatAddress(account)}
                    </span>
                  </div>
                </div>
              )}
            </div>
        </div>
      </main>

      {/* Toast Notifications */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: 'white',
            },
          },
          error: {
            iconTheme: {
              primary: '#EF4444',
              secondary: 'white',
            },
          },
        }}
      />
    </div>
  );
}

export default FountainPage;
